import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SUPPORTED_LOCALES,
  type ActivePlatformBroadcast,
  type PlatformBroadcastListItem,
  type PlatformBroadcastTranslation,
  type PlatformBroadcastTranslationAssistMode,
  type PlatformBroadcastTranslationAssistResult,
  type PlatformBroadcastTranslations,
  type SupportedLocale,
} from '@accestra/types';
import AlertBanner from '../ui/AlertBanner';
import {
  Button,
  C,
  Card,
  Field,
  Pill,
  friendlyError,
  formatMaybeDateTime,
  inputStyle,
  paFetch,
  textareaStyle,
  toDateTimeLocalInput,
  toNullableIsoString,
} from './partner-program/shared';
import {
  applyBroadcastTranslationAssistResult,
  areGeneratedBroadcastTranslationsStale,
  isGeneratedBroadcastTranslationLocaleStale,
} from '../../lib/platform-broadcast-translations';

type BroadcastStatus = 'draft' | 'scheduled' | 'active' | 'archived';
type BroadcastSeverity = 'info' | 'success' | 'warning' | 'critical';
type BroadcastLifecycle = 'draft' | 'scheduled' | 'active' | 'expired' | 'archived';

const BROADCAST_STATUS_VALUES: BroadcastStatus[] = ['draft', 'scheduled', 'active', 'archived'];
const BROADCAST_SEVERITY_VALUES: BroadcastSeverity[] = ['info', 'success', 'warning', 'critical'];

interface BroadcastListResponse {
  broadcasts: PlatformBroadcastListItem[];
  total: number;
  page: number;
  pageSize: number;
}

interface BroadcastDetailResponse {
  broadcast: PlatformBroadcastListItem;
}

type BroadcastTranslationAssistResponse = PlatformBroadcastTranslationAssistResult;

type BroadcastFormTranslations = Record<SupportedLocale, PlatformBroadcastTranslation>;

interface BroadcastFormState {
  status: BroadcastStatus;
  severity: BroadcastSeverity;
  audienceScope: 'all_users';
  ctaUrl: string;
  startAt: string;
  endAt: string;
  isDismissible: boolean;
  translations: BroadcastFormTranslations;
}

const STATUS_FILTER_OPTIONS: Array<BroadcastStatus | 'all'> = [
  'all',
  'draft',
  'scheduled',
  'active',
  'archived',
];

const LIFECYCLE_FILTER_OPTIONS: Array<BroadcastLifecycle | 'all'> = [
  'all',
  'draft',
  'scheduled',
  'active',
  'expired',
  'archived',
];

const SEVERITY_FILTER_OPTIONS: Array<BroadcastSeverity | 'all'> = [
  'all',
  'critical',
  'warning',
  'success',
  'info',
];
const TRANSLATION_TARGET_LOCALES = SUPPORTED_LOCALES.filter((locale) => locale !== 'en');

function emptyTranslation(): PlatformBroadcastTranslation {
  return {
    title: '',
    body: '',
    ctaLabel: '',
  };
}

function emptyForm(): BroadcastFormState {
  return {
    status: 'draft',
    severity: 'info',
    audienceScope: 'all_users',
    ctaUrl: '',
    startAt: '',
    endAt: '',
    isDismissible: true,
    translations: {
      en: emptyTranslation(),
      de: emptyTranslation(),
      fr: emptyTranslation(),
      es: emptyTranslation(),
      it: emptyTranslation(),
    },
  };
}

function toFormTranslations(input: PlatformBroadcastTranslations): BroadcastFormTranslations {
  return {
    en: {
      title: input.en?.title ?? '',
      body: input.en?.body ?? '',
      ctaLabel: input.en?.ctaLabel ?? '',
    },
    de: {
      title: input.de?.title ?? '',
      body: input.de?.body ?? '',
      ctaLabel: input.de?.ctaLabel ?? '',
    },
    fr: {
      title: input.fr?.title ?? '',
      body: input.fr?.body ?? '',
      ctaLabel: input.fr?.ctaLabel ?? '',
    },
    es: {
      title: input.es?.title ?? '',
      body: input.es?.body ?? '',
      ctaLabel: input.es?.ctaLabel ?? '',
    },
    it: {
      title: input.it?.title ?? '',
      body: input.it?.body ?? '',
      ctaLabel: input.it?.ctaLabel ?? '',
    },
  };
}

function toFormState(broadcast: PlatformBroadcastListItem): BroadcastFormState {
  return {
    status: broadcast.status,
    severity: broadcast.severity,
    audienceScope: broadcast.audienceScope,
    ctaUrl: broadcast.ctaUrl ?? '',
    startAt: toDateTimeLocalInput(broadcast.startAt),
    endAt: toDateTimeLocalInput(broadcast.endAt),
    isDismissible: broadcast.isDismissible,
    translations: toFormTranslations(broadcast.translations),
  };
}

function normalizeTranslations(
  translations: BroadcastFormTranslations,
): PlatformBroadcastTranslations {
  const normalized: PlatformBroadcastTranslations = {};

  for (const locale of SUPPORTED_LOCALES) {
    const entry = translations[locale];
    const title = entry.title?.trim() || null;
    const body = entry.body?.trim() || null;
    const ctaLabel = entry.ctaLabel?.trim() || null;

    if (title || body || ctaLabel) {
      normalized[locale] = { title, body, ctaLabel };
    }
  }

  return normalized;
}

function resolvePreviewContent(
  translations: BroadcastFormTranslations,
  locale: SupportedLocale,
): ActivePlatformBroadcast['content'] {
  const english = translations.en;
  const preferred = translations[locale];
  const preferredTitle = preferred.title?.trim() ?? '';
  const preferredBody = preferred.body?.trim() ?? '';
  const preferredCta = preferred.ctaLabel?.trim() ?? '';
  const englishTitle = english.title?.trim() ?? '';
  const englishBody = english.body?.trim() ?? '';
  const englishCta = english.ctaLabel?.trim() ?? '';
  const title = preferredTitle || englishTitle || '—';
  const body = preferredBody || englishBody || '—';

  return {
    locale,
    title,
    body,
    ctaLabel: preferredCta || englishCta || null,
    usedFallback: locale !== 'en' && (
      !preferredTitle
      || !preferredBody
      || (!!englishCta && !preferredCta)
    ),
  };
}

function lifecycleTone(lifecycle: BroadcastLifecycle): string {
  switch (lifecycle) {
    case 'active':
      return C.success;
    case 'scheduled':
      return C.warning;
    case 'expired':
    case 'archived':
      return C.muted;
    default:
      return C.accent;
  }
}

function severityVariant(severity: BroadcastSeverity): 'info' | 'success' | 'warning' | 'danger' {
  switch (severity) {
    case 'success':
      return 'success';
    case 'warning':
      return 'warning';
    case 'critical':
      return 'danger';
    default:
      return 'info';
  }
}

export default function PlatformBroadcastsPanel() {
  const { t } = useTranslation(['platformAdmin', 'common']);
  const [statusFilter, setStatusFilter] = useState<BroadcastStatus | 'all'>('all');
  const [severityFilter, setSeverityFilter] = useState<BroadcastSeverity | 'all'>('all');
  const [lifecycleFilter, setLifecycleFilter] = useState<BroadcastLifecycle | 'all'>('all');
  const [broadcasts, setBroadcasts] = useState<PlatformBroadcastListItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedBroadcast, setSelectedBroadcast] = useState<PlatformBroadcastListItem | null>(null);
  const [form, setForm] = useState<BroadcastFormState>(emptyForm);
  const [editingLocale, setEditingLocale] = useState<SupportedLocale>('en');
  const [previewLocale, setPreviewLocale] = useState<SupportedLocale>('en');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [translating, setTranslating] = useState(false);
  const [translationFeedback, setTranslationFeedback] = useState<string | null>(null);
  const [translationError, setTranslationError] = useState<string | null>(null);
  const [generatedTranslationSources, setGeneratedTranslationSources] = useState<Partial<Record<SupportedLocale, string>>>({});

  const previewContent = useMemo(
    () => resolvePreviewContent(form.translations, previewLocale),
    [form.translations, previewLocale],
  );

  const isNewDraft = selectedBroadcast === null;
  const translatedLocales = useMemo(
    () => SUPPORTED_LOCALES.filter((locale) => {
      const entry = form.translations[locale];
      return Boolean(entry.title?.trim() || entry.body?.trim() || entry.ctaLabel?.trim());
    }),
    [form.translations],
  );
  const englishReadyForTranslation = Boolean(
    form.translations.en.title?.trim() && form.translations.en.body?.trim(),
  );
  const hasNonEnglishContent = useMemo(
    () => TRANSLATION_TARGET_LOCALES.some((locale) => {
      const entry = form.translations[locale];
      return Boolean(entry.title?.trim() || entry.body?.trim() || entry.ctaLabel?.trim());
    }),
    [form.translations],
  );
  const translationsStale = useMemo(
    () => areGeneratedBroadcastTranslationsStale({
      english: form.translations.en,
      generatedSourceSignatures: generatedTranslationSources,
    }),
    [form.translations.en, generatedTranslationSources],
  );

  function resetTranslationAssistState() {
    setTranslating(false);
    setTranslationFeedback(null);
    setTranslationError(null);
    setGeneratedTranslationSources({});
  }

  async function loadBroadcasts() {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: '1',
        pageSize: '50',
      });

      if (statusFilter !== 'all') {
        params.set('status', statusFilter);
      }

      if (severityFilter !== 'all') {
        params.set('severity', severityFilter);
      }

      if (lifecycleFilter !== 'all') {
        params.set('lifecycle', lifecycleFilter);
      }

      const result = await paFetch<BroadcastListResponse>(
        'GET',
        `/platform-admin/broadcasts?${params.toString()}`,
      );
      setBroadcasts(result.broadcasts);

      if (selectedId && result.broadcasts.some((broadcast) => broadcast.id === selectedId)) {
        return;
      }

      if (result.broadcasts[0]) {
        setSelectedId(result.broadcasts[0].id);
      } else {
        setSelectedId(null);
        setSelectedBroadcast(null);
        setForm(emptyForm());
        resetTranslationAssistState();
      }
    } catch (nextError) {
      setError(friendlyError(nextError));
    } finally {
      setLoading(false);
    }
  }

  async function loadBroadcast(id: string) {
    try {
      const result = await paFetch<BroadcastDetailResponse>('GET', `/platform-admin/broadcasts/${id}`);
      setSelectedBroadcast(result.broadcast);
      setForm(toFormState(result.broadcast));
      setEditingLocale('en');
      setPreviewLocale('en');
      resetTranslationAssistState();
    } catch (nextError) {
      setError(friendlyError(nextError));
    }
  }

  useEffect(() => {
    void loadBroadcasts();
  }, [statusFilter, severityFilter, lifecycleFilter]);

  useEffect(() => {
    if (!selectedId) {
      return;
    }

    void loadBroadcast(selectedId);
  }, [selectedId]);

  function updateTranslation(
    locale: SupportedLocale,
    field: keyof PlatformBroadcastTranslation,
    value: string,
  ) {
    setForm((current) => ({
      ...current,
      translations: {
        ...current.translations,
        [locale]: {
          ...current.translations[locale],
          [field]: value,
        },
      },
    }));

    if (locale !== 'en') {
      setGeneratedTranslationSources((current) => {
        const next = { ...current };
        delete next[locale];
        return next;
      });
    }
  }

  async function handleSave() {
    setSaving(true);
    setFeedback(null);
    setError(null);
    try {
      const payload = {
        status: form.status,
        severity: form.severity,
        audienceScope: form.audienceScope,
        translations: normalizeTranslations(form.translations),
        ctaUrl: form.ctaUrl.trim() || null,
        startAt: toNullableIsoString(form.startAt),
        endAt: toNullableIsoString(form.endAt),
        isDismissible: form.isDismissible,
      };

      const result = isNewDraft
        ? await paFetch<BroadcastDetailResponse>('POST', '/platform-admin/broadcasts', payload)
        : await paFetch<BroadcastDetailResponse>('PATCH', `/platform-admin/broadcasts/${selectedBroadcast.id}`, payload);

      await loadBroadcasts();
      setSelectedId(result.broadcast.id);
      setSelectedBroadcast(result.broadcast);
      setForm(toFormState(result.broadcast));
      setFeedback(isNewDraft
        ? t('platformAdmin:broadcasts.feedback.created')
        : t('platformAdmin:broadcasts.feedback.updated'));
    } catch (nextError) {
      setError(friendlyError(nextError));
    } finally {
      setSaving(false);
    }
  }

  async function handleDuplicate() {
    if (!selectedBroadcast) {
      return;
    }

    setDuplicating(true);
    setFeedback(null);
    setError(null);
    try {
      const result = await paFetch<BroadcastDetailResponse>(
        'POST',
        `/platform-admin/broadcasts/${selectedBroadcast.id}/duplicate`,
      );
      await loadBroadcasts();
      setSelectedId(result.broadcast.id);
      setSelectedBroadcast(result.broadcast);
      setForm(toFormState(result.broadcast));
      setFeedback(t('platformAdmin:broadcasts.feedback.duplicated'));
    } catch (nextError) {
      setError(friendlyError(nextError));
    } finally {
      setDuplicating(false);
    }
  }

  async function handleGenerateTranslations(mode: PlatformBroadcastTranslationAssistMode) {
    if (!englishReadyForTranslation) {
      setTranslationError(t('platformAdmin:broadcasts.translations.englishRequired'));
      setTranslationFeedback(null);
      return;
    }

    if (
      mode === 'replace_existing'
      && hasNonEnglishContent
      && !window.confirm(t('platformAdmin:broadcasts.translations.overwriteConfirm'))
    ) {
      return;
    }

    setTranslating(true);
    setTranslationFeedback(null);
    setTranslationError(null);

    try {
      const result = await paFetch<BroadcastTranslationAssistResponse>(
        'POST',
        '/platform-admin/broadcasts/translate',
        {
          mode,
          translations: form.translations,
        },
      );
      const applied = applyBroadcastTranslationAssistResult({
        current: form.translations,
        result,
      });
      const appliedCount = applied.generatedLocales.length;
      const preservedCount = Object.values(result.locales)
        .filter((localeResult) => localeResult && localeResult.preservedFields.length > 0)
        .length;
      const failedCount = Object.values(result.locales)
        .filter((localeResult) => localeResult?.status === 'failed')
        .length;

      setForm((current) => ({
        ...current,
        translations: applied.translations,
      }));
      setGeneratedTranslationSources((current) => {
        const next = { ...current };
        for (const locale of applied.generatedLocales) {
          next[locale] = result.sourceSignature;
        }
        return next;
      });

      if (appliedCount === 0) {
        setTranslationFeedback(t('platformAdmin:broadcasts.feedback.noTranslationChanges'));
      } else {
        const feedbackParts = [
          t('platformAdmin:broadcasts.feedback.translationsApplied', { count: appliedCount }),
        ];

        if (mode === 'fill_empty' && preservedCount > 0) {
          feedbackParts.push(t('platformAdmin:broadcasts.feedback.translationsPreserved'));
        }

        setTranslationFeedback(feedbackParts.join(' '));
      }

      if (failedCount > 0) {
        setTranslationError(t('platformAdmin:broadcasts.feedback.translationsNeedReview', { count: failedCount }));
      }
    } catch (nextError) {
      setTranslationError(friendlyError(nextError));
    } finally {
      setTranslating(false);
    }
  }

  function startNewDraft() {
    setSelectedId(null);
    setSelectedBroadcast(null);
    setForm(emptyForm());
    setEditingLocale('en');
    setPreviewLocale('en');
    setFeedback(null);
    setError(null);
    resetTranslationAssistState();
  }

  function localeMetaLabel(locale: SupportedLocale): string | null {
    if (locale === 'en') {
      return t('platformAdmin:broadcasts.translations.required');
    }

    if (generatedTranslationSources[locale]) {
      return isGeneratedBroadcastTranslationLocaleStale({
        english: form.translations.en,
        sourceSignature: generatedTranslationSources[locale],
      })
        ? t('platformAdmin:broadcasts.translations.stale')
        : t('platformAdmin:broadcasts.translations.generated');
    }

    return null;
  }

  const editingLocaleGenerated = editingLocale !== 'en' && Boolean(generatedTranslationSources[editingLocale]);
  const editingLocaleStale = editingLocale !== 'en' && isGeneratedBroadcastTranslationLocaleStale({
    english: form.translations.en,
    sourceSignature: generatedTranslationSources[editingLocale],
  });

  return (
    <section style={{ marginBottom: 32 }}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
          {t('platformAdmin:broadcasts.sectionEyebrow')}
        </div>
        <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
          {t('platformAdmin:broadcasts.sectionSubtitle')}
        </div>
      </div>

      <div className="acc-platform-admin-split">
        <Card
          title={t('platformAdmin:broadcasts.listTitle')}
          subtitle={t('platformAdmin:broadcasts.listSubtitle')}
          action={(
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <Button onClick={startNewDraft}>
                {t('platformAdmin:broadcasts.actions.newDraft')}
              </Button>
              <Button onClick={() => void loadBroadcasts()}>
                {t('common:actions.refresh')}
              </Button>
            </div>
          )}
        >
          <div className="acc-platform-admin-toolbar">
            <Field label={t('platformAdmin:broadcasts.filters.status')}>
              <select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value as BroadcastStatus | 'all')}
                style={inputStyle}
              >
                {STATUS_FILTER_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option === 'all'
                      ? t('platformAdmin:broadcasts.filters.allStatuses')
                      : t(`platformAdmin:broadcasts.status.${option}`)}
                  </option>
                ))}
              </select>
            </Field>

            <Field label={t('platformAdmin:broadcasts.filters.severity')}>
              <select
                value={severityFilter}
                onChange={(event) => setSeverityFilter(event.target.value as BroadcastSeverity | 'all')}
                style={inputStyle}
              >
                {SEVERITY_FILTER_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option === 'all'
                      ? t('platformAdmin:broadcasts.filters.allSeverities')
                      : t(`platformAdmin:broadcasts.severity.${option}`)}
                  </option>
                ))}
              </select>
            </Field>

            <Field label={t('platformAdmin:broadcasts.filters.lifecycle')}>
              <select
                value={lifecycleFilter}
                onChange={(event) => setLifecycleFilter(event.target.value as BroadcastLifecycle | 'all')}
                style={inputStyle}
              >
                {LIFECYCLE_FILTER_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option === 'all'
                      ? t('platformAdmin:broadcasts.filters.allLifecycles')
                      : t(`platformAdmin:broadcasts.lifecycle.${option}`)}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {loading ? (
            <div style={{ color: C.muted, fontSize: 13 }}>
              {t('platformAdmin:tables.loading')}
            </div>
          ) : broadcasts.length === 0 ? (
            <div className="acc-platform-admin-empty">
              <div style={{ fontSize: 15, fontWeight: 760, color: C.text }}>
                {t('platformAdmin:broadcasts.empty.title')}
              </div>
              <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.65 }}>
                {t('platformAdmin:broadcasts.empty.body')}
              </div>
            </div>
          ) : (
            <div className="acc-platform-admin-list">
              {broadcasts.map((broadcast) => {
                const localeLabel = broadcast.translatedLocales.length > 0
                  ? broadcast.translatedLocales.map((locale) => t(`common:locales.${locale}`)).join(', ')
                  : t('platformAdmin:broadcasts.localeCoverage.none');

                return (
                  <button
                    key={broadcast.id}
                    type="button"
                    className={selectedId === broadcast.id ? 'acc-platform-admin-list-item acc-platform-admin-list-item--active' : 'acc-platform-admin-list-item'}
                    onClick={() => setSelectedId(broadcast.id)}
                  >
                    <div className="acc-platform-admin-list-item__header">
                      <div>
                        <div className="acc-platform-admin-list-item__title">
                          {broadcast.translations.en?.title ?? t('platformAdmin:broadcasts.untitled')}
                        </div>
                        <div className="acc-platform-admin-list-item__meta">
                          {t(`platformAdmin:broadcasts.status.${broadcast.status}`)} · {t(`platformAdmin:broadcasts.severity.${broadcast.severity}`)}
                        </div>
                      </div>
                      <Pill
                        label={t(`platformAdmin:broadcasts.lifecycle.${broadcast.lifecycle}`)}
                        tone={lifecycleTone(broadcast.lifecycle)}
                      />
                    </div>
                    <div className="acc-platform-admin-list-item__grid">
                      <div>
                        <span>{t('platformAdmin:broadcasts.listWindow')}</span>
                        <strong>
                          {broadcast.startAt
                            ? formatMaybeDateTime(broadcast.startAt)
                            : t('platformAdmin:broadcasts.immediate')}
                        </strong>
                      </div>
                      <div>
                        <span>{t('platformAdmin:broadcasts.listLocales')}</span>
                        <strong>{localeLabel}</strong>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </Card>

        <div className="acc-platform-admin-detail-stack">
          {error && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, padding: '10px 12px', color: C.danger, fontSize: 13 }}>
              {error}
            </div>
          )}
          {feedback && (
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '10px 12px', color: C.success, fontSize: 13 }}>
              {feedback}
            </div>
          )}

          <Card
            title={isNewDraft ? t('platformAdmin:broadcasts.editor.newTitle') : t('platformAdmin:broadcasts.editor.editTitle')}
            subtitle={t('platformAdmin:broadcasts.editor.subtitle')}
            action={selectedBroadcast ? (
              <Button
                variant="ghost"
                onClick={() => void handleDuplicate()}
                disabled={duplicating || saving}
              >
                {duplicating
                  ? t('platformAdmin:broadcasts.actions.duplicating')
                  : t('platformAdmin:broadcasts.actions.duplicate')}
              </Button>
            ) : undefined}
          >
            <div className="acc-platform-admin-toolbar">
              <Field label={t('platformAdmin:broadcasts.fields.status')}>
                <select
                  value={form.status}
                  onChange={(event) => setForm((current) => ({ ...current, status: event.target.value as BroadcastStatus }))}
                  style={inputStyle}
                >
                  {BROADCAST_STATUS_VALUES.map((option) => (
                    <option key={option} value={option}>
                      {t(`platformAdmin:broadcasts.status.${option}`)}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label={t('platformAdmin:broadcasts.fields.severity')}>
                <select
                  value={form.severity}
                  onChange={(event) => setForm((current) => ({ ...current, severity: event.target.value as BroadcastSeverity }))}
                  style={inputStyle}
                >
                  {BROADCAST_SEVERITY_VALUES.map((option) => (
                    <option key={option} value={option}>
                      {t(`platformAdmin:broadcasts.severity.${option}`)}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label={t('platformAdmin:broadcasts.fields.audience')}>
                <select
                  value={form.audienceScope}
                  onChange={(event) => setForm((current) => ({ ...current, audienceScope: event.target.value as 'all_users' }))}
                  style={inputStyle}
                >
                  <option value="all_users">
                    {t('platformAdmin:broadcasts.audience.allUsers')}
                  </option>
                </select>
              </Field>
            </div>

            <div className="acc-platform-admin-toolbar">
              <Field label={t('platformAdmin:broadcasts.fields.startAt')}>
                <input
                  type="datetime-local"
                  style={inputStyle}
                  value={form.startAt}
                  onChange={(event) => setForm((current) => ({ ...current, startAt: event.target.value }))}
                />
              </Field>

              <Field label={t('platformAdmin:broadcasts.fields.endAt')}>
                <input
                  type="datetime-local"
                  style={inputStyle}
                  value={form.endAt}
                  onChange={(event) => setForm((current) => ({ ...current, endAt: event.target.value }))}
                />
              </Field>

              <Field label={t('platformAdmin:broadcasts.fields.ctaUrl')}>
                <input
                  style={inputStyle}
                  value={form.ctaUrl}
                  onChange={(event) => setForm((current) => ({ ...current, ctaUrl: event.target.value }))}
                  placeholder="https://"
                />
              </Field>
            </div>

            <label className="acc-platform-admin-checkbox">
              <input
                type="checkbox"
                checked={form.isDismissible}
                onChange={(event) => setForm((current) => ({ ...current, isDismissible: event.target.checked }))}
              />
              <span>
                <strong>{t('platformAdmin:broadcasts.fields.dismissible')}</strong>
                <small>{t('platformAdmin:broadcasts.fields.dismissibleHint')}</small>
              </span>
            </label>

            <div className="acc-platform-admin-translation-card">
              <div className="acc-platform-admin-translation-card__header">
                <div>
                  <div className="acc-platform-admin-translation-card__title">
                    {t('platformAdmin:broadcasts.translations.title')}
                  </div>
                  <div className="acc-platform-admin-translation-card__body">
                    {t('platformAdmin:broadcasts.translations.subtitle')}
                  </div>
                  <div className="acc-platform-admin-translation-assist__note">
                    {t('platformAdmin:broadcasts.translations.sourceHelp')}
                  </div>
                </div>
                <div className="acc-platform-admin-translation-card__actions">
                  <Pill
                    label={t('platformAdmin:broadcasts.localeCoverage.count', { count: translatedLocales.length })}
                    tone={translatedLocales.length > 1 ? C.success : C.muted}
                  />
                  <div className="acc-platform-admin-translation-assist__buttons">
                    {hasNonEnglishContent && (
                      <Button
                        variant="ghost"
                        onClick={() => void handleGenerateTranslations('replace_existing')}
                        disabled={translating || !englishReadyForTranslation}
                      >
                        {translating
                          ? t('platformAdmin:broadcasts.actions.generatingTranslations')
                          : t('platformAdmin:broadcasts.actions.refreshTranslations')}
                      </Button>
                    )}
                    <Button
                      onClick={() => void handleGenerateTranslations('fill_empty')}
                      disabled={translating || !englishReadyForTranslation}
                    >
                      {translating
                        ? t('platformAdmin:broadcasts.actions.generatingTranslations')
                        : t('platformAdmin:broadcasts.actions.generateTranslations')}
                    </Button>
                  </div>
                </div>
              </div>

              {(translationFeedback || translationError || translationsStale) && (
                <div className="acc-platform-admin-translation-assist__status">
                  {translationFeedback && (
                    <div className="acc-platform-admin-translation-assist__status-line acc-platform-admin-translation-assist__status-line--success">
                      {translationFeedback}
                    </div>
                  )}
                  {translationError && (
                    <div className="acc-platform-admin-translation-assist__status-line acc-platform-admin-translation-assist__status-line--error">
                      {translationError}
                    </div>
                  )}
                  {translationsStale && (
                    <div className="acc-platform-admin-translation-assist__status-line acc-platform-admin-translation-assist__status-line--warning">
                      {t('platformAdmin:broadcasts.translations.staleHint')}
                    </div>
                  )}
                </div>
              )}

              <div className="acc-platform-admin-locale-tabs">
                {SUPPORTED_LOCALES.map((locale) => (
                  <button
                    key={locale}
                    type="button"
                    className={editingLocale === locale ? 'acc-platform-admin-locale-tab acc-platform-admin-locale-tab--active' : 'acc-platform-admin-locale-tab'}
                    onClick={() => setEditingLocale(locale)}
                  >
                    <span>{t(`common:locales.${locale}`)}</span>
                    {localeMetaLabel(locale) ? (
                      <strong className={generatedTranslationSources[locale] && isGeneratedBroadcastTranslationLocaleStale({
                        english: form.translations.en,
                        sourceSignature: generatedTranslationSources[locale],
                      }) ? 'acc-platform-admin-locale-tab__meta acc-platform-admin-locale-tab__meta--stale' : 'acc-platform-admin-locale-tab__meta'}>
                        {localeMetaLabel(locale)}
                      </strong>
                    ) : null}
                  </button>
                ))}
              </div>

              {editingLocaleGenerated && (
                <div className="acc-platform-admin-translation-assist__locale-note">
                  {editingLocaleStale
                    ? t('platformAdmin:broadcasts.translations.generatedStaleHint')
                    : t('platformAdmin:broadcasts.translations.generatedHint')}
                </div>
              )}

              <div className="acc-platform-admin-toolbar acc-platform-admin-toolbar--stacked">
                <Field label={t('platformAdmin:broadcasts.fields.title')}>
                  <input
                    style={inputStyle}
                    value={form.translations[editingLocale].title ?? ''}
                    onChange={(event) => updateTranslation(editingLocale, 'title', event.target.value)}
                  />
                </Field>

                <Field label={t('platformAdmin:broadcasts.fields.body')}>
                  <textarea
                    style={{ ...textareaStyle, minHeight: 130 }}
                    value={form.translations[editingLocale].body ?? ''}
                    onChange={(event) => updateTranslation(editingLocale, 'body', event.target.value)}
                  />
                </Field>

                <Field label={t('platformAdmin:broadcasts.fields.ctaLabel')}>
                  <input
                    style={inputStyle}
                    value={form.translations[editingLocale].ctaLabel ?? ''}
                    onChange={(event) => updateTranslation(editingLocale, 'ctaLabel', event.target.value)}
                  />
                </Field>
              </div>

              {editingLocale !== 'en' && (
                <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
                  {t('platformAdmin:broadcasts.translations.fallbackHint')}
                </div>
              )}
            </div>

            <div className="acc-platform-admin-action-row">
              <div style={{ display: 'grid', gap: 4 }}>
                {selectedBroadcast ? (
                  <>
                    <div style={{ fontSize: 12, color: C.muted }}>
                      {t('platformAdmin:broadcasts.editor.versionLabel', { version: selectedBroadcast.version })}
                    </div>
                    <div style={{ fontSize: 12, color: C.muted }}>
                      {t('platformAdmin:broadcasts.editor.updatedAtLabel', {
                        updatedAt: formatMaybeDateTime(selectedBroadcast.updatedAt),
                      })}
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: 12, color: C.muted }}>
                    {t('platformAdmin:broadcasts.editor.newDraftHint')}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <Button variant="ghost" onClick={startNewDraft}>
                  {t('platformAdmin:broadcasts.actions.reset')}
                </Button>
                <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
                  {saving
                    ? t('platformAdmin:broadcasts.actions.saving')
                    : isNewDraft
                      ? t('platformAdmin:broadcasts.actions.create')
                      : t('platformAdmin:broadcasts.actions.save')}
                </Button>
              </div>
            </div>
          </Card>

          <Card
            title={t('platformAdmin:broadcasts.preview.title')}
            subtitle={t('platformAdmin:broadcasts.preview.subtitle')}
            action={(
              <select
                value={previewLocale}
                onChange={(event) => setPreviewLocale(event.target.value as SupportedLocale)}
                style={{ ...inputStyle, minWidth: 160 }}
              >
                {SUPPORTED_LOCALES.map((locale) => (
                  <option key={locale} value={locale}>
                    {t(`common:locales.${locale}`)}
                  </option>
                ))}
              </select>
            )}
          >
            <div style={{ display: 'grid', gap: 12 }}>
              <AlertBanner variant={severityVariant(form.severity)}>
                <div className="acc-platform-broadcast-preview">
                  <div className="acc-platform-broadcast-preview__copy">
                    <div className="acc-platform-broadcast-preview__title">
                      {previewContent.title}
                    </div>
                    <div className="acc-platform-broadcast-preview__body">
                      {previewContent.body}
                    </div>
                  </div>
                  <div className="acc-platform-broadcast-preview__actions">
                    {previewContent.ctaLabel && form.ctaUrl.trim() ? (
                      <Button variant="primary" title={form.ctaUrl.trim()}>
                        {previewContent.ctaLabel}
                      </Button>
                    ) : null}
                    {form.isDismissible ? (
                      <Button variant="ghost">
                        {t('platformAdmin:broadcasts.preview.dismissAction')}
                      </Button>
                    ) : (
                      <Pill
                        label={t('platformAdmin:broadcasts.preview.persistent')}
                        tone={C.muted}
                      />
                    )}
                  </div>
                </div>
              </AlertBanner>

              <div className="acc-platform-admin-summary-grid">
                <div className="acc-platform-admin-summary-card">
                  <span>{t('platformAdmin:broadcasts.preview.lifecycle')}</span>
                  <strong>{selectedBroadcast ? t(`platformAdmin:broadcasts.lifecycle.${selectedBroadcast.lifecycle}`) : t(`platformAdmin:broadcasts.status.${form.status}`)}</strong>
                </div>
                <div className="acc-platform-admin-summary-card">
                  <span>{t('platformAdmin:broadcasts.preview.scope')}</span>
                  <strong>{t('platformAdmin:broadcasts.audience.allUsers')}</strong>
                </div>
                <div className="acc-platform-admin-summary-card">
                  <span>{t('platformAdmin:broadcasts.preview.locale')}</span>
                  <strong>{t(`common:locales.${previewLocale}`)}</strong>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
}
